import type {EventLog, LogActionType} from "~/domainModels/logs";
import type {OttopilotEventContainerCudLog} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/ottopilotEventContainersWorkspaceCapability/domain/OttopilotEventContainer";
import type {EventContainerTemplate} from "~/domainModels/workspaceModules/eventContainersModule";
import type {OrganizationalUnit} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/organizationalUnitsWorkspaceCapability/domain/OrganizationalUnit";
import type {OrganizationalUnitTemplate} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/organizationalUnitsWorkspaceCapability/domain/OrganizationalUnitTemplate";
import type {User} from "@vesta/vos-types/modules/workspaceLevel/users/domain/User";
import type {UserTemplate} from "@vesta/vos-types/modules/workspaceLevel/users/domain/UserTemplate";
import type {OttopilotEventTemplate} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/ottopilotEventsWorkspaceCapability/domain/OttopilotEventTemplate";
import type {Iso8601DateTime, Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import type {EventContainerWithEvents} from "~/utilities/apis/api";
import type {PlatformUserDisplayDetails} from "~/utilities/utilities";
import type {GeographyContextData} from "~/contexts/geographyContext";

export enum ActivityCardType {
    eventType = "eventType",
    dispositionChange = "dispositionChange",
    agentChange = "agentChange",
    detailsChange = "detailsChange",
}

export type ActivityCreatedByKind = "user" | "system";

export type ActivityCardCreatedBy = {
    kind: ActivityCreatedByKind;
    userId: Uuid | null;
};

export type EventTypeActivityCardData = {
    cardType: ActivityCardType.eventType;
    id: Uuid;
    timestamp: Iso8601DateTime;
    actionType: LogActionType;
    eventId: Uuid;
    eventTemplateId: Uuid;
    eventIndex: number;
    createdBy: ActivityCardCreatedBy;
    statusRaw?: string | null;
    sourceRaw?: string | null;
    simpleText?: string | null;
    remarks?: string | null;
};

export type DispositionChangeActivityCardData = {
    cardType: ActivityCardType.dispositionChange;
    id: Uuid;
    timestamp: Iso8601DateTime;
    oldDispositionId: Uuid | null;
    newDispositionId: Uuid | null;
    createdBy: ActivityCardCreatedBy;
    remarks?: string | null;
};

export type AgentChangeActivityCardData = {
    cardType: ActivityCardType.agentChange;
    id: Uuid;
    timestamp: Iso8601DateTime;
    columnId: Uuid;
    columnLabel: string;
    oldValue: unknown;
    newValue: unknown;
    createdBy: ActivityCardCreatedBy;
    remarks?: string | null;
};

export type ActivityFieldChangeLine = {
    fieldKey: string;
    fieldLabel: string;
    oldValue: unknown;
    newValue: unknown;
};

export type DetailsChangeActivityCardData = {
    cardType: ActivityCardType.detailsChange;
    id: Uuid;
    timestamp: Iso8601DateTime;
    entityLabel: string;
    eventTemplateId?: Uuid;
    fieldChanges: Array<ActivityFieldChangeLine>;
    createdBy: ActivityCardCreatedBy;
    remarks?: string | null;
    isDelete?: boolean;
    /** Legacy single-field fallback when `fieldChanges` is empty. */
    fieldLabel?: string;
    oldValue?: unknown;
    newValue?: unknown;
};

export type ActivityCardData =
    | EventTypeActivityCardData
    | DispositionChangeActivityCardData
    | AgentChangeActivityCardData
    | DetailsChangeActivityCardData;

export type LogCardCreatedBy = {
    kind: ActivityCreatedByKind;
    displayName: string;
    roleLabel?: string;
};

export type LogCardFieldChangeLine = {
    fieldLabel: string;
    oldDisplay: string;
    newDisplay: string;
};

export type LogCardData = {
    id: Uuid;
    cardType: ActivityCardType;
    timestamp: Iso8601DateTime;
    status?: string;
    entityAction: string;
    context: string;
    /** Text before old/new strikethrough pair, e.g. "Disposition changed to ". */
    contextPrefix?: string;
    /** When set with `contextNewValue`, renders old value struck through per Figma. */
    contextOldValue?: string;
    contextNewValue?: string;
    contextFieldChanges?: Array<LogCardFieldChangeLine>;
    remarks?: string;
    createdBy: LogCardCreatedBy;
    ou?: string;
    source?: string;
    /** Journey link pill label in card header, e.g. "J1". */
    journeyLabel?: string;
    journeyCount?: number;
    /** When true, card is visually de-emphasized (activity from an inactive lifecycle/journey). */
    isInactive?: boolean;
    /** Customer profile: lifecycle this activity row belongs to (for status lookup). */
    lifecycleId?: Uuid;
};

export type TimelineDayGroup = {
    label: string;
    dateKey: string;
    items: LogCardData[];
    /** When false, the day section starts collapsed. Defaults to open. */
    defaultOpen?: boolean;
};

export type ActivityTimelineWorkspaceConfig = {
    tryAmaronLevelColumns?: boolean;
    agentColumnIds?: Array<Uuid>;
    remarksColumnId?: Uuid | null;
    dispositionColumnIds?: Array<Uuid>;
    platformColumnIds?: Array<Uuid>;
    journeyUtmSourceColumnIds?: Array<Uuid>;
    eventUtmSourceColumnIds?: Array<Uuid>;
};

export type ActivityTimelineResolverContext = {
    eventContainer: EventContainerWithEvents;
    eventLogs: Array<EventLog>;
    eventContainerLogs: Array<OttopilotEventContainerCudLog>;
    eventTemplates: Array<OttopilotEventTemplate>;
    eventContainerTemplate: EventContainerTemplate;
    users: Array<User>;
    userTemplates: Array<UserTemplate>;
    organizationalUnits: Array<OrganizationalUnit>;
    organizationalUnitTemplates: Array<OrganizationalUnitTemplate>;
    platformUserDetailsByUserId: ReadonlyMap<Uuid, PlatformUserDisplayDetails>;
    userDefinedObjectOptionsByTemplateId: ReadonlyMap<Uuid, ReadonlyArray<{displayName: string; id: Uuid}>>;
    geography?: GeographyContextData | null;
    workspaceSpecific?: ActivityTimelineWorkspaceConfig;
};

export type ActivityTimelineBuildContext = ActivityTimelineResolverContext;
